/**
 * useMouseConnection - 鼠标设备连接 Hook
 *
 * 与 useKeyboardConnection 风格对齐，提供统一的鼠标设备连接管理
 *
 * @example
 * // 基础使用
 * const { status, device, connect } = useMouseConnection();
 *
 * @example
 * // 自动连接
 * const { status, device, battery } = useMouseConnection({ autoConnect: true });
 *
 * @example
 * // 带回调
 * const { connect } = useMouseConnection({
 *   onConnected: (device) => console.log('已连接:', device),
 *   onDisconnected: () => console.log('已断开'),
 *   onError: (error) => console.error('连接失败:', error),
 * });
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { mouseService } from "../../../main-app/src/sdkApi/mouse/commonApi";
import {
  getMouseHidFilters,
  isSupportedMouseHidDevice,
} from "@eweadn-qiankun/shared-utils";
import {
  type BatteryInfo,
  type DeviceInfo,
  type MouseSolutionProvider,
} from "@eweadn-qiankun/shared-types";

// ==================== 类型定义 ====================

export type MouseConnectionStatus =
  | "disconnected" // 未连接
  | "connecting" // 连接中
  | "ready" // 已连接，设备就绪
  | "sleeping" // 已连接，设备休眠
  | "error"; // 连接错误

export interface MouseDeviceState {
  /** 设备基础信息 (CID/MID/Type) */
  info: DeviceInfo | null;
  /** 电量信息 */
  battery: BatteryInfo | null;
  /** 是否有线连接 */
  isWired: boolean;
  /** 是否在线 */
  isOnline: boolean;
  /** 是否休眠 */
  isSleeping: boolean;
  /** 当前方案 */
  provider: MouseSolutionProvider;
  /** 方案名称 */
  providerName: string;
}

export interface UseMouseConnectionResult {
  /** 连接状态 */
  status: MouseConnectionStatus;
  /** 设备状态 */
  device: MouseDeviceState | null;
  /** 错误信息 */
  errorMsg: string;
  /** 连接设备 */
  connect: (hidDevice?: HIDDevice) => Promise<boolean>;
  /** 断开连接 */
  disconnect: () => Promise<void>;
  /** 重置状态 */
  reset: () => void;
  /** 刷新设备状态 */
  refresh: () => void;
}

export interface UseMouseConnectionOptions {
  /** 鼠标方案，默认 jiangmeng */
  provider?: MouseSolutionProvider;
  /** 自动连接（Hook 挂载后立即尝试连接） */
  autoConnect?: boolean;
  /** 自动刷新间隔（毫秒），0 表示不自动刷新，默认 2000 */
  refreshInterval?: number;
  /** 连接成功回调 */
  onConnected?: (device: MouseDeviceState) => void;
  /** 断开连接回调 */
  onDisconnected?: () => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
  /** 休眠状态变化回调 */
  onSleepChange?: (sleeping: boolean) => void;
  /** 电量变化回调 */
  onBatteryChange?: (battery: BatteryInfo) => void;
}

// ==================== WebHID 类型定义 ====================

interface HIDDevice {
  vendorId: number;
  productId: number;
  productName: string;
  serialNumber?: string;
  opened?: boolean;
  collections?: Array<{ usagePage: number; usage: number }>;
  open?: () => Promise<void>;
  close?: () => Promise<void>;
}

interface HID {
  getDevices(): Promise<HIDDevice[]>;
  requestDevice(options?: {
    filters: Array<{ vendorId?: number; productId?: number }>;
  }): Promise<HIDDevice[]>;
}

function supportsHID(nav: Navigator): nav is Navigator & { hid: HID } {
  return "hid" in nav && nav.hid !== undefined;
}

// ==================== Hook 实现 ====================

export function useMouseConnection(
  options: UseMouseConnectionOptions = {}
): UseMouseConnectionResult {
  const {
    provider = "jiangmeng",
    autoConnect = false,
    refreshInterval = 2000,
    onConnected,
    onDisconnected,
    onError,
    onSleepChange,
    onBatteryChange,
  } = options;

  const [status, setStatus] = useState<MouseConnectionStatus>("disconnected");
  const [device, setDevice] = useState<MouseDeviceState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // 用于追踪上一次的状态，触发回调
  const prevSleepingRef = useRef<boolean | null>(null);
  const prevBatteryRef = useRef<BatteryInfo | null>(null);
  const autoConnectStartedRef = useRef(false);

  /**
   * 从 mouseService 读取设备状态
   */
  const readDeviceState = useCallback((): MouseDeviceState | null => {
    if (!mouseService.isInitialized()) {
      return null;
    }

    const info = mouseService.getHandle()?.deviceInfo?.info ?? null;
    const battery = mouseService.getBattery();
    const isWired = mouseService.isWired();
    const isOnline = mouseService.isOnline();
    const isSleeping = mouseService.isSleeping();

    return {
      info: info ? { cid: info.cid, mid: info.mid, type: info.type } : null,
      battery,
      isWired,
      isOnline,
      isSleeping,
      provider: mouseService.getProvider(),
      providerName: mouseService.getProviderName(),
    };
  }, []);

  /**
   * 刷新设备状态
   */
  const refresh = useCallback(() => {
    const state = readDeviceState();
    if (!state) return;

    setDevice(state);

    // 更新连接状态
    if (state.isSleeping) {
      setStatus("sleeping");
    } else if (state.isOnline) {
      setStatus("ready");
    }

    // 休眠状态变化回调
    if (
      prevSleepingRef.current !== null &&
      prevSleepingRef.current !== state.isSleeping
    ) {
      onSleepChange?.(state.isSleeping);
    }
    prevSleepingRef.current = state.isSleeping;

    // 电量变化回调
    if (
      state.battery &&
      prevBatteryRef.current &&
      prevBatteryRef.current.level !== state.battery.level
    ) {
      onBatteryChange?.(state.battery);
    }
    prevBatteryRef.current = state.battery;
  }, [readDeviceState, onSleepChange, onBatteryChange]);

  /**
   * 连接设备
   */
  const connect = useCallback(
    async (hidDevice?: HIDDevice): Promise<boolean> => {
      setStatus("connecting");
      setErrorMsg("");

      try {
        // 1. 检查浏览器支持
        if (!supportsHID(navigator)) {
          throw new Error("浏览器不支持 WebHID API");
        }

        // 2. 初始化 mouseService
        await mouseService.init(provider);

        // 3. 获取或请求设备
        let targetDevice: HIDDevice | undefined = hidDevice;
        if (!targetDevice) {
          // 先尝试获取已授权设备
          const devices = await navigator.hid.getDevices();
          const mouseDevices = devices.filter((d: HIDDevice) =>
            isSupportedMouseHidDevice(d)
          );

          if (mouseDevices.length > 0) {
            targetDevice = mouseDevices[0];
          } else {
            // 请求用户选择设备
            const requested = await navigator.hid.requestDevice({
              filters: getMouseHidFilters(),
            });
            if (requested.length === 0) {
              throw new Error("未选择设备");
            }
            targetDevice = requested[0];
          }
        }

        if (!targetDevice) {
          throw new Error("未找到设备");
        }

        // 4. 连接设备
        const connected = await mouseService.connect(targetDevice);
        if (!connected) {
          throw new Error("设备连接失败");
        }

        // 5. 等待设备稳定
        await new Promise((resolve) => setTimeout(resolve, 500));

        // 6. 读取设备状态
        const state = readDeviceState();
        if (!state) {
          throw new Error("无法读取设备状态");
        }

        setDevice(state);
        setStatus(state.isSleeping ? "sleeping" : "ready");
        onConnected?.(state);

        return true;
      } catch (error: unknown) {
        const err = error as Error;
        const message = err.message || "连接失败，请重试";
        setStatus("error");
        setErrorMsg(message);
        onError?.(err);
        console.error("[useMouseConnection] 连接失败:", error);
        return false;
      }
    },
    [provider, readDeviceState, onConnected, onError]
  );

  /**
   * 断开连接
   */
  const disconnect = useCallback(async () => {
    try {
      await mouseService.close();
    } catch (error) {
      console.error("[useMouseConnection] 断开连接失败:", error);
    } finally {
      setStatus("disconnected");
      setDevice(null);
      onDisconnected?.();
    }
  }, [onDisconnected]);

  /**
   * 重置状态
   */
  const reset = useCallback(() => {
    setStatus("disconnected");
    setDevice(null);
    setErrorMsg("");
    prevSleepingRef.current = null;
    prevBatteryRef.current = null;
  }, []);

  // 自动连接
  useEffect(() => {
    if (!autoConnect) {
      autoConnectStartedRef.current = false;
      return;
    }

    if (status !== "disconnected" || autoConnectStartedRef.current) {
      return;
    }

    autoConnectStartedRef.current = true;
    void connect().finally(() => {
      autoConnectStartedRef.current = false;
    });
  }, [autoConnect, status, connect]);

  // 自动刷新
  useEffect(() => {
    if (refreshInterval > 0 && (status === "ready" || status === "sleeping")) {
      const timer = setInterval(refresh, refreshInterval);
      return () => clearInterval(timer);
    }
  }, [refreshInterval, status, refresh]);

  return {
    status,
    device,
    errorMsg,
    connect,
    disconnect,
    reset,
    refresh,
  };
}

export default useMouseConnection;
